import { useEffect, useRef, useState } from 'react'
import NavBar from '../components/NavBar'
import MemberPanel from '../components/MemberPanel'
import ChatPanel from '../components/ChatPanel'
import SettingsModal from '../components/SettingsModal'
import MyInfoModal from '../components/MyInfoModal'
import { AuthState } from '../store/auth'
import { roomStore, RoomInfo, RoomMember, ChatEntry, subscribeRoom } from '../store/room'
import { apiRoomDetail } from '../server/ServiceCall'
import * as hub from '../hub/roomHub'
import '../css/App.css'

type RoomTab = 'members' | 'info' | 'chat'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    function onResize() {
      clearTimeout(timer)
      timer = setTimeout(() => setIsMobile(window.innerWidth < 768), 100)
    }
    window.addEventListener('resize', onResize)
    return () => { clearTimeout(timer); window.removeEventListener('resize', onResize) }
  }, [])
  return isMobile
}

interface Props {
  authState:   AuthState
  roomCode:    string
  onLeave:     () => void
  onLogout:    () => void
  onGameStart: (roomCode: string, matchId?: number) => void
  // SEQ=10147 — TTR 게임 시작 분기 콜백. RoomHub.StartGame 브로드캐스트 페이로드 gameType=TICKET 시 사용.
  onGameStartTicket?: (roomCode: string, matchId?: number) => void
  // SEQ=10217 — 루미큐브 게임 시작 분기 콜백. RoomHub.StartGame 브로드캐스트 페이로드 gameType=RUMK 시 사용.
  onGameStartRummikub?: (roomCode: string, matchId?: number) => void
  onNicknameChanged: (newNickname: string) => void
}

export default function Room({ authState, roomCode, onLeave, onLogout, onGameStart, onGameStartTicket, onGameStartRummikub, onNicknameChanged }: Props) {
  const isMobile = useIsMobile()
  const [activeTab,   setActiveTab]   = useState<RoomTab>('info')
  const [chatBadge,   setChatBadge]   = useState(0)
  const prevChatLen                   = useRef(0)
  const [roomInfo,    setRoomInfo]    = useState<RoomInfo | null>(roomStore.getRoom())
  const [members,     setMembers]     = useState<RoomMember[]>(roomStore.getMembers())
  const [chatLog,     setChatLog]     = useState<ChatEntry[]>(roomStore.getChatLog())
  const [errMsg,      setErrMsg]      = useState('')
  const [leaving,     setLeaving]     = useState(false)
  const [transferTo,  setTransferTo]  = useState<number>(0)
  const [showSettings, setShowSettings] = useState(false)  // SEQ=10110
  const [showMyInfo,   setShowMyInfo]   = useState(false)  // SEQ=10110

  const myUserCode = authState.userCode
  const isHost = roomInfo ? roomInfo.hostUserCode === myUserCode : false
  // SEQ=10147 — 게임별 최소 인원 분기. CATAN=3, TICKET=2.
  // SEQ=10217 — RUMK=2 (루미큐브 SoT §1 인원 2~4 정합)
  const minSeats = (() => {
    const gt = (roomInfo?.gameType ?? 'CATAN').toUpperCase()
    if (gt === 'TICKET') return 2
    if (gt === 'RUMK')   return 2
    return 3
  })()

  // ── store 구독 ────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeRoom(() => {
      setRoomInfo(roomStore.getRoom())
      setMembers(roomStore.getMembers())
      setChatLog(roomStore.getChatLog())
    })
    return () => { unsub() }
  }, [])

  // ── 모바일 채팅 배지 카운트 ──────────────────────────
  useEffect(() => {
    if (!isMobile) { setChatBadge(0); return }
    if (activeTab === 'chat') {
      prevChatLen.current = chatLog.length
      setChatBadge(0)
    } else {
      const newCount = chatLog.length - prevChatLen.current
      if (newCount > 0) setChatBadge(newCount)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatLog, activeTab, isMobile])

  // ── 안전망: 진입 시 store에 룸 없으면 REST로 sync ──────
  useEffect(() => {
    if (roomStore.getRoom()) return
    apiRoomDetail(authState.token, roomCode).then(r => {
      if (r.resultCode !== '00') return
      try {
        const parsed = JSON.parse(r.resultData)
        if (parsed?.room && parsed?.members) roomStore.applyDetail(parsed)
      } catch { /* no-op */ }
    }).catch(() => { /* no-op */ })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode])

  // ── SignalR 이벤트 바인딩 ──────────────────────────────
  useEffect(() => {
    function onMemberJoined(data: unknown) {
      const d = data as { userCode: number; nickname: string; seatIndex: number; seatType?: string; joinedAt: string }
      const seatType = String(d.seatType ?? 'PLAYER').toUpperCase() === 'OBSERVER' ? 'OBSERVER' : 'PLAYER'
      roomStore.addMember({
        userCode:  d.userCode,
        nickname:  d.nickname,
        seatIndex: d.seatIndex,
        seatType,
        isHost:    false,
        joinedAt:  d.joinedAt,
      })
      const label = seatType === 'OBSERVER' ? '관전 입장했습니다' : '입장했습니다'
      roomStore.appendSystemChat(`${d.nickname} 님이 ${label}.`, 'MEMBER_ENTER')
    }

    // SEQ=10072 STEP B — 좌석 타입 이동 브로드캐스트
    function onSeatChanged(data: unknown) {
      const d = data as { userCode: number; seatType: string; seatIndex: number }
      const seatType = String(d.seatType ?? 'PLAYER').toUpperCase() === 'OBSERVER' ? 'OBSERVER' : 'PLAYER'
      roomStore.updateSeat(d.userCode, seatType, d.seatIndex)
      const nick = roomStore.getMembers().find(m => m.userCode === d.userCode)?.nickname
      if (nick) {
        const label = seatType === 'OBSERVER' ? '관전석으로 이동했습니다' : '플레이석으로 이동했습니다'
        roomStore.appendSystemChat(`${nick} 님이 ${label}.`, 'MEMBER_ENTER')
      }
    }

    function onMemberLeft(data: unknown) {
      const d = data as {
        userCode:       number
        hostChanged:    boolean
        newHostUserCode?: number | null
        roomDeleted:    boolean
      }
      // 본인이 퇴장당한 경우 (강퇴 등 서버 정책) — 현재 Phase 1엔 없음, 방어 코드
      const leavingNick = members.find(m => m.userCode === d.userCode)?.nickname ?? '(알 수 없음)'
      roomStore.removeMember(d.userCode)
      roomStore.appendSystemChat(`${leavingNick} 님이 퇴장했습니다.`, 'MEMBER_LEAVE')

      if (d.hostChanged && d.newHostUserCode) {
        roomStore.updateHost(d.newHostUserCode)
        const newHostNick = roomStore.getMembers().find(m => m.userCode === d.newHostUserCode)?.nickname
        if (newHostNick) roomStore.appendSystemChat(`${newHostNick} 님이 방장이 됐습니다.`, 'HOST_CHANGE')
      }
      if (d.roomDeleted) {
        roomStore.appendSystemChat('모든 멤버가 퇴장하여 방이 삭제됩니다.', 'ROOM_DELETED')
        setTimeout(() => { roomStore.clearRoom(); onLeave() }, 1500)
      }
    }

    function onHostChanged(data: unknown) {
      const d = data as { newHostUserCode: number }
      roomStore.updateHost(d.newHostUserCode)
      const newHostNick = roomStore.getMembers().find(m => m.userCode === d.newHostUserCode)?.nickname
      if (newHostNick) roomStore.appendSystemChat(`${newHostNick} 님이 방장이 됐습니다.`, 'HOST_CHANGE')
    }

    function onChatReceived(data: unknown) {
      const d = data as { userCode: number; nickname: string; message: string; timestamp: string }
      roomStore.appendChat({
        userCode:  d.userCode,
        nickname:  d.nickname,
        message:   d.message,
        timestamp: d.timestamp.slice(11, 16),
        isSystem:  false,
      })
    }

    function onOptionsUpdated(data: unknown) {
      const d = data as { optionsJson: string }
      const cur = roomStore.getRoom()
      if (cur) {
        try {
          roomStore.setRoom({ ...cur, options: JSON.parse(d.optionsJson) })
        } catch { /* no-op */ }
      }
    }

    function onGameStarting(data: unknown) {
      // SEQ=10147 — gameType 분기 (CATAN | TICKET). 기존 클라(gameType 미수신)는 CATAN 기본.
      // SEQ=10217 — RUMK 분기 추가 (onGameStartRummikub 콜백 TTR 패턴 정합)
      const d = data as { roomCode: string; matchId?: number; gameType?: string }
      const gameType = String(d.gameType ?? 'CATAN').toUpperCase()
      roomStore.appendSystemChat('게임이 시작됩니다!', 'GAME_START')
      if (gameType === 'TICKET' && onGameStartTicket) {
        onGameStartTicket(d.roomCode, d.matchId)
      } else if (gameType === 'RUMK' && onGameStartRummikub) {
        onGameStartRummikub(d.roomCode, d.matchId)
      } else {
        onGameStart(d.roomCode, d.matchId)
      }
    }

    function onRoomDetail(data: unknown) {
      const d = data as Parameters<typeof roomStore.applyDetail>[0]
      roomStore.applyDetail(d)
    }

    function onError(data: unknown) {
      const d = data as { msg?: string }
      setErrMsg(d.msg ?? '오류 발생')
      setTimeout(() => setErrMsg(''), 4000)
    }

    function onForceLogout() {
      import('../store/auth').then(({ auth }) => {
        auth.clear()
        window.dispatchEvent(new CustomEvent('session-expired'))
      })
    }

    hub.on('MemberJoined',   onMemberJoined   as (...a: unknown[]) => void)
    hub.on('MemberLeft',     onMemberLeft     as (...a: unknown[]) => void)
    hub.on('SeatChanged',    onSeatChanged    as (...a: unknown[]) => void)
    hub.on('HostChanged',    onHostChanged    as (...a: unknown[]) => void)
    hub.on('ChatReceived',   onChatReceived   as (...a: unknown[]) => void)
    hub.on('OptionsUpdated', onOptionsUpdated as (...a: unknown[]) => void)
    hub.on('GameStarting',   onGameStarting   as (...a: unknown[]) => void)
    hub.on('RoomDetail',     onRoomDetail     as (...a: unknown[]) => void)
    hub.on('Error',          onError          as (...a: unknown[]) => void)
    hub.on('ForceLogout',    onForceLogout    as (...a: unknown[]) => void)

    return () => {
      hub.off('MemberJoined',   onMemberJoined   as (...a: unknown[]) => void)
      hub.off('MemberLeft',     onMemberLeft     as (...a: unknown[]) => void)
      hub.off('SeatChanged',    onSeatChanged    as (...a: unknown[]) => void)
      hub.off('HostChanged',    onHostChanged    as (...a: unknown[]) => void)
      hub.off('ChatReceived',   onChatReceived   as (...a: unknown[]) => void)
      hub.off('OptionsUpdated', onOptionsUpdated as (...a: unknown[]) => void)
      hub.off('GameStarting',   onGameStarting   as (...a: unknown[]) => void)
      hub.off('RoomDetail',     onRoomDetail     as (...a: unknown[]) => void)
      hub.off('Error',          onError          as (...a: unknown[]) => void)
      hub.off('ForceLogout',    onForceLogout    as (...a: unknown[]) => void)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, onLeave, onGameStart])

  // ── 방 나가기 ──────────────────────────────────────────
  async function handleLeave() {
    if (!window.confirm('방에서 나가시겠습니까?')) return
    setLeaving(true)
    try {
      await hub.roomLeave(roomCode)
    } catch { /* no-op */ }
    roomStore.clearRoom()
    onLeave()
  }

  // ── 게임 시작 ─────────────────────────────────────────
  async function handleStartGame() {
    setErrMsg('')
    try {
      await hub.startGame(roomCode)
    } catch (e: unknown) {
      setErrMsg((e as Error).message ?? '게임 시작 실패')
    }
  }

  // ── 좌석 이동 (옵저버↔플레이석, 비진행 중) — SEQ=10072 STEP B ─────
  async function handleTakeSeat() {
    setErrMsg('')
    try {
      await hub.changeSeatType(roomCode, 'PLAYER')
    } catch (e: unknown) {
      setErrMsg((e as Error).message ?? '좌석 이동 실패')
    }
  }
  async function handleLeaveSeat() {
    setErrMsg('')
    try {
      await hub.changeSeatType(roomCode, 'OBSERVER')
    } catch (e: unknown) {
      setErrMsg((e as Error).message ?? '좌석 이동 실패')
    }
  }

  // ── 방장 양도 ─────────────────────────────────────────
  async function handleTransfer() {
    if (!transferTo) return
    try {
      await hub.hostTransfer(roomCode, transferTo)
      setTransferTo(0)
    } catch (e: unknown) {
      setErrMsg((e as Error).message ?? '양도 실패')
    }
  }

  // ── 채팅 전송 ─────────────────────────────────────────
  async function handleSendChat(message: string) {
    try {
      await hub.sendChat(roomCode, message)
    } catch { /* no-op */ }
  }

  // ── 룸코드 복사 ───────────────────────────────────────
  function copyCode() {
    navigator.clipboard.writeText(roomCode).catch(() => {})
  }

  // ── 로그아웃 ──────────────────────────────────────────
  async function handleLogout() {
    await hub.stopConnection()
    roomStore.clearRoom()
    onLogout()
  }

  if (!roomInfo) {
    return (
      <div className="app-shell">
        <NavBar
          auth={authState}
          onLogout={handleLogout}
          onOpenSettings={() => setShowSettings(true)}
          onOpenMyInfo={() => setShowMyInfo(true)}
        />
        <div className="room-loading"><span className="spinner" /> 룸 정보 로드 중…</div>
        {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
        {showMyInfo && (
          <MyInfoModal
            authState={authState}
            onNicknameChanged={onNicknameChanged}
            onClose={() => setShowMyInfo(false)}
          />
        )}
      </div>
    )
  }

  const otherMembers = members.filter(m => m.userCode !== myUserCode)

  // 탭 전환 핸들러 (채팅 탭 진입 시 배지 리셋)
  function handleTabChange(tab: RoomTab) {
    setActiveTab(tab)
    if (tab === 'chat') {
      prevChatLen.current = chatLog.length
      setChatBadge(0)
    }
  }

  // 옵션 패널 내용 — 데스크톱/모바일 공용
  const optionsPanelContent = (
    <>
      <h4 className="panel-title">방 정보</h4>

      <div className="option-row">
        <span className="option-label">게임</span>
        <span className="option-val">카탄</span>
      </div>
      <div className="option-row">
        <span className="option-label">정원</span>
        <span className="option-val">{roomInfo.currentSeats} / {roomInfo.maxSeats}</span>
      </div>
      <div className="option-row">
        <span className="option-label">공개 여부</span>
        <span className="option-val">{roomInfo.isPrivate ? '비공개' : '공개'}</span>
      </div>
      <div className="option-row">
        <span className="option-label">룸코드</span>
        <span className="option-val code-display">
          {roomInfo.roomCode}
          <button className="btn-copy" onClick={copyCode} title="복사">복사</button>
        </span>
      </div>
      <div className="option-row">
        <span className="option-label">상태</span>
        <span className="option-val">{roomInfo.status === 'WAITING' ? '대기 중' : roomInfo.status}</span>
      </div>

      {/* 방장 전용 컨트롤 */}
      {isHost && (
        <div className="host-controls">
          <hr className="divider" />
          <button
            className="btn-start"
            onClick={handleStartGame}
            disabled={roomInfo.currentSeats < minSeats}
            title={roomInfo.currentSeats < minSeats ? `최소 ${minSeats}명 필요 (현재 ${roomInfo.currentSeats}명)` : ''}
          >
            게임 시작
          </button>

          {otherMembers.length > 0 && (
            <div className="transfer-row">
              <select
                className="transfer-select"
                value={transferTo}
                onChange={e => setTransferTo(Number(e.target.value))}
              >
                <option value={0}>-- 방장 양도 대상 --</option>
                {otherMembers.map(m => (
                  <option key={m.userCode} value={m.userCode}>{m.nickname}</option>
                ))}
              </select>
              <button
                className="btn-transfer"
                onClick={handleTransfer}
                disabled={!transferTo}
              >
                양도
              </button>
            </div>
          )}
        </div>
      )}

      <div className="leave-area">
        <button className="btn-leave" onClick={handleLeave} disabled={leaving}>
          {leaving ? <span className="spinner" /> : null}방 나가기
        </button>
      </div>
    </>
  )

  return (
    <div className="app-shell">
      <NavBar
        auth={authState}
        onLogout={handleLogout}
        onOpenSettings={() => setShowSettings(true)}
        onOpenMyInfo={() => setShowMyInfo(true)}
      />

      {errMsg && <div className="room-err-banner">{errMsg}</div>}

      {/* 모바일 탭바 */}
      {isMobile && (
        <div className="room-tab-bar">
          <button
            className={`room-tab-btn${activeTab === 'members' ? ' active' : ''}`}
            onClick={() => handleTabChange('members')}
          >
            멤버
          </button>
          <button
            className={`room-tab-btn${activeTab === 'info' ? ' active' : ''}`}
            onClick={() => handleTabChange('info')}
          >
            방 정보
          </button>
          <button
            className={`room-tab-btn${activeTab === 'chat' ? ' active' : ''}`}
            onClick={() => handleTabChange('chat')}
          >
            채팅
            {chatBadge > 0 && (
              <span className="room-tab-badge">{chatBadge > 99 ? '99+' : chatBadge}</span>
            )}
          </button>
        </div>
      )}

      <div className="room-layout">
        {/* 좌측 — 멤버 패널 (isMobile 탭 active 클래스는 MemberPanel 내부 div에 전달) */}
        <MemberPanel
          members={members}
          maxSeats={roomInfo.maxSeats}
          myUserCode={myUserCode}
          tabActive={isMobile && activeTab === 'members'}
          roomStatus={roomInfo.status}
          onTakeSeat={handleTakeSeat}
          onLeaveSeat={handleLeaveSeat}
        />

        {/* 중앙 — 옵션 패널 */}
        <div className={`options-panel${isMobile && activeTab === 'info' ? ' tab-active' : ''}`}>
          {optionsPanelContent}
        </div>

        {/* 우측 — 채팅 패널 */}
        <ChatPanel
          chatLog={chatLog}
          roomCode={roomCode}
          onSend={handleSendChat}
          onClear={() => roomStore.clearChatLog()}
          tabActive={isMobile && activeTab === 'chat'}
        />
      </div>

      {/* SEQ=10110 STEP=4 — 설정 / 내정보 모달 */}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showMyInfo && (
        <MyInfoModal
          authState={authState}
          onNicknameChanged={onNicknameChanged}
          onClose={() => setShowMyInfo(false)}
        />
      )}
    </div>
  )
}
